// Defaults for local `npm run dev` (no NEXT_PUBLIC_* env vars set). For
// cluster builds the per-env workflow bakes the real URL into the bundle
// at npm-run-build time. Ports match the .NET services' launchSettings.json.
export const DEFAULT_AGENT_API_BASE_URL = "http://localhost:5004";
export const DEFAULT_REPOSITORY_FILE_ACCESS_API_BASE_URL = "http://localhost:5123";

const ABSOLUTE_URL_PATTERN = /^[a-z][a-z0-9+.-]*:\/\//i;

function normalizeBaseUrl(value: string | undefined, fallback: string) {
  const trimmed = value?.trim();
  if (!trimmed) {
    return fallback;
  }

  if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith("//")) {
    return `https:${trimmed}`;
  }

  if (trimmed.startsWith("/")) {
    try {
      const fallbackUrl = new URL(fallback);
      return new URL(trimmed, `${fallbackUrl.protocol}//${fallbackUrl.host}`).toString();
    } catch {
      return fallback;
    }
  }

  const lower = trimmed.toLowerCase();
  const inferredScheme =
    lower.startsWith("localhost") ||
    lower.startsWith("127.") ||
    lower.startsWith("0.0.0.0") ||
    lower.startsWith("[::1]");

  const scheme = inferredScheme ? "http" : "https";
  return `${scheme}://${trimmed}`;
}

export function getAgentApiBaseUrl() {
  return normalizeBaseUrl(
    process.env.NEXT_PUBLIC_AGENT_API_BASE_URL,
    DEFAULT_AGENT_API_BASE_URL,
  );
}

/**
 * Base URL used by server-side code (route handlers, server components) to
 * reach the backend API. Prefers the cluster-internal address so calls don't
 * leave the cluster; falls back to the public URL when not set (e.g. in
 * development where only `NEXT_PUBLIC_AGENT_API_BASE_URL` is defined).
 */
export function getServerAgentApiBaseUrl() {
  return normalizeBaseUrl(
    process.env.AGENT_API_BASE_URL ?? process.env.NEXT_PUBLIC_AGENT_API_BASE_URL,
    DEFAULT_AGENT_API_BASE_URL,
  );
}

export function buildServerAgentApiUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, getServerAgentApiBaseUrl()).toString();
}

export function getRepositoryFileAccessApiBaseUrl() {
  return normalizeBaseUrl(
    process.env.NEXT_PUBLIC_REPOSITORY_FILE_ACCESS_API_BASE_URL,
    DEFAULT_REPOSITORY_FILE_ACCESS_API_BASE_URL,
  );
}

export function buildAgentApiUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, getAgentApiBaseUrl()).toString();
}

export function buildRepositoryFileAccessApiUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, getRepositoryFileAccessApiBaseUrl()).toString();
}
